import * as Sentry from "@sentry/react";

/**
 * Sentry configuration for error tracking and performance monitoring
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

// Environment detection
const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;
const environment = import.meta.env.MODE || "development";
const appVersion = import.meta.env.VITE_APP_VERSION || "1.0.0";

/**
 * Initialize Sentry for the application
 */
export function initSentry(): void {
  // Don't initialize in development mode unless explicitly enabled
  if (isDevelopment && !import.meta.env.VITE_ENABLE_SENTRY_IN_DEV) {
    console.log("[Sentry] Disabled in development mode");
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    release: `omnibase@${appVersion}`,

    // Debug mode in non-production
    debug: !isProduction,

    // Performance Monitoring
    // In production: sample 10% of transactions
    // In staging/development: sample 100% for debugging
    tracesSampleRate: isProduction ? 0.1 : 1.0,

    // Session Replay (optional - can be enabled later)
    // replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    // replaysOnErrorSampleRate: isProduction ? 1.0 : 1.0,
    // integrations: [Sentry.replayIntegration()],

    // Error filtering
    beforeSend(event, hint) {
      // Filter out specific errors that shouldn't be reported
      const exception = hint.originalException;

      if (shouldIgnoreError(exception)) {
        return null;
      }

      // Sanitize sensitive data
      event = sanitizeEvent(event);

      // Add custom tags
      if (!event.tags) {
        event.tags = {};
      }
      event.tags.build_mode = environment;
      event.tags.is_production = String(isProduction);

      return event;
    },

    // Set user context automatically
    initialScope: {
      tags: {
        app: "omnibase",
      },
    },
  });

  console.log(`[Sentry] Initialized in ${environment} mode`);
}

/**
 * Check if an error should be ignored
 */
function shouldIgnoreError(error: unknown): boolean {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);

  // Ignore known non-critical errors
  const ignoredPatterns = [
    // Network errors that are often transient
    "Network Error",
    "Failed to fetch",
    "Network request failed",
    "AbortError",

    // Browser extension errors
    "chrome-extension",
    "moz-extension",

    // Third-party script errors
    "Script error",
  ];

  return ignoredPatterns.some((pattern) =>
    errorMessage.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * Sanitize event to remove sensitive data
 */
function sanitizeEvent(event: Sentry.Event): Sentry.Event {
  // Remove user IP
  if (event.request) {
    event.request = {
      ...event.request,
      headers: sanitizeHeaders(event.request.headers),
    };
  }

  // Sanitize user context
  if (event.user) {
    event.user = {
      ...event.user,
      // Only keep non-sensitive identifiers
      id: event.user.id,
      // Remove sensitive fields
      email: undefined,
      username: undefined,
      ip_address: undefined,
    };
  }

  return event;
}

/**
 * Sanitize request headers
 */
function sanitizeHeaders(
  headers: Record<string, string> | undefined
): Record<string, string> | undefined {
  if (!headers) return undefined;

  const sanitized = { ...headers };
  const sensitiveHeaders = [
    "authorization",
    "cookie",
    "x-auth-token",
    "x-api-key",
    "password",
  ];

  for (const key of Object.keys(sanitized)) {
    if (sensitiveHeaders.some((sh) => key.toLowerCase().includes(sh))) {
      sanitized[key] = "[REDACTED]";
    }
  }

  return sanitized;
}

/**
 * Set user context (sanitized)
 */
export function setUser(userId: string | null): void {
  Sentry.setUser(userId ? { id: userId } : null);
}

/**
 * Manually capture an exception
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (context) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture a message
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info"
): void {
  Sentry.captureMessage(message, level);
}

/**
 * Start a performance transaction
 */
export function startTransaction(
  name: string,
  op: string
): ReturnType<typeof Sentry.startInactiveSpan> | null {
  return Sentry.startInactiveSpan({ name, op });
}

/**
 * Create a wrapped component for error boundaries
 */
export const withErrorBoundary = Sentry.withErrorBoundary;

/**
 * Export Sentry types for convenience
 */
export type { SeverityLevel } from "@sentry/react";
